import React, { useCallback, useMemo } from 'react';
import { View, StyleSheet, FlatList, ListRenderItem } from 'react-native';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { Event } from '@/types/residents/event';
import { strings } from '@/constants/residents';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchEvents } from '@/store/slices/residents/homeSlice';
import { selectSortedEvents } from '@/store/selectors/residentsEventsSelectors';
import {
  PageHeader,
  EventGlassEmptyState,
  DesktopEventGlassCard,
} from '@/components/residents/redesign';
import { DesktopEventsScreenSkeleton } from './DesktopEventsScreenSkeleton';

export const DesktopEventsScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((s) => s.home.isLoading);
  const events = useAppSelector(selectSortedEvents);

  React.useEffect(() => {
    dispatch(fetchEvents());
  }, [dispatch]);

  const renderItem: ListRenderItem<Event> = useCallback(({ item }) => (
    <View style={styles.centerColumn}>
      <DesktopEventGlassCard event={item} />
    </View>
  ), []);

  const keyExtractor = useCallback((item: Event) => String(item.id), []);

  const ListHeaderComponent = useMemo(() => (
    <PageHeader title="Events" />
  ), []);

  if (isLoading) {
    return (
      <View style={styles.root}>
        <DesktopEventsScreenSkeleton />
      </View>
    );
  }

  if (!isLoading && events.length === 0) {
    return (
      <View style={[styles.root, styles.content]}>
        {ListHeaderComponent}
        <EventGlassEmptyState
          title={strings.events.empty.title}
          message={strings.events.empty.description}
        />
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <FlatList
        data={events}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ListHeaderComponent={ListHeaderComponent}
        ItemSeparatorComponent={ItemSeparator}
        contentContainerStyle={styles.content}
        removeClippedSubviews={true}
        windowSize={7}
        initialNumToRender={4}
        maxToRenderPerBatch={6}
      />
    </View>
  );
};

const ItemSeparator = () => <View style={styles.itemSeparator} />;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  content: {
    padding: REDESIGN_SPACING.horizontalPadding,
    paddingBottom: 40,
  },
  centerColumn: {
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
  },
  itemSeparator: {
    height: 16,
  },
});

export default DesktopEventsScreen;
